<template>
  <div
    class="dui-date-dropdowns"
    :disabled="disabled"
  >
    <DuiDropdown
      v-if="displayDay"
      v-model="day"
      class="day-dropdown"
      :options="availableDays"
      placeholder="Day"
      @change="updateSelectedDate"
    />

    <DuiDropdown
      v-if="displayMonth"
      v-model="month"
      class="month-dropdown"
      :class="{ 'wider': useMonthNames }"
      :options="availableMonths"
      placeholder="Month"
      @change="updateSelectedDate"
    />

    <DuiDropdown
      v-if="displayYear"
      v-model="year"
      class="year-dropdown"
      :options="availableYears"
      placeholder="Year"
      @change="updateSelectedDate"
    />
  </div>
</template>

<script>
import moment from 'moment';

const MONTH_30_DAYS = [
  '4',
  '6',
  '9',
  '11',
];

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function _getDays(maxDay) {
  const days = [];

  for (let i=1; i<=maxDay; i++) {
    days.push({
      text: _getTwoDigitDatePart(i),
      value: i.toString(),
    });
  }

  return days;
}

function _getMonths(useMonthNames) {
  const months = [];

  MONTH_NAMES.forEach((monthName, idx) => {
    months.push({
      text: useMonthNames ? monthName : _getTwoDigitDatePart(idx + 1),
      value: (idx + 1).toString(),
    });
  });

  return months;
}

function _getTwoDigitDatePart(datePartValue) {
  if (!datePartValue) {
    return '01';
  }

  let datePart = datePartValue.toString();
  datePart = (datePart.length > 1) ? datePart : `0${ datePart }`;
  return datePart;
}

function _getYears(minYear, maxYear) {
  const years = [];

  for (let i=maxYear; i >= minYear; i--) {
    years.push({
      text: i.toString(),
      value: i.toString(),
    });
  }

  return years;
}

export default {
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    displayDay: {
      type: Boolean,
      default: true,
    },
    displayMonth: {
      type: Boolean,
      default: true,
    },
    displayYear: {
      type: Boolean,
      default: true,
    },
    format: {
      type: String,
      default: 'YYYY-MM-DD',
    },
    maxYear: {
      type: Number,
      default: moment().year(),
    },
    minYear: {
      type: Number,
      default: 1950,
    },
    useMonthNames: {
      type: Boolean,
      default: true,
    },
    value: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    availableMonths: [],
    availableYears: [],
    day: null,
    month: null,
    selectedDate: null,
    year: null,
  }),
  computed: {
    availableDays() {
      if (!this.displayDay) {
        return null;
      }

      if (this.month === '2') {
        if (!this.displayYear || (this.year % 4) === 0) {
          return _getDays(29);
        }

        return _getDays(28);
      }

      if (MONTH_30_DAYS.indexOf(this.month) > -1) {
        return _getDays(30);
      }

      return _getDays(31);
    },
  },
  watch: {
    month: function() {
      this.checkValidDay();
    },

    value: function() {
      this.initDate();
    },

    year: function() {
      this.checkValidDay();
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    checkValidDay() {
      if (!this.displayDay) {
        return;
      }

      if (parseInt(this.day) > this.availableDays.length) {
        this.$set(this, 'day', '1');
      }
    },

    init() {
      this.initDropdownOptions();
      this.initDate();
    },

    initDate() {
      let initialDate;

      if (!this.value) {
        initialDate = moment();
      } else {
        initialDate = moment(this.value, this.format);
      }

      this.$set(this, 'day', initialDate.date().toString());
      this.$set(this, 'month', (initialDate.month() + 1).toString());
      this.$set(this, 'year', initialDate.year().toString());
      this.updateSelectedDate();
    },

    initDropdownOptions() {
      if (this.displayMonth) {
        this.$set(this, 'availableMonths', _getMonths(this.useMonthNames));
      }

      if (this.displayYear) {
        this.$set(this, 'availableYears', _getYears(this.minYear, this.maxYear));
      }
    },

    updateSelectedDate() {
      const day = _getTwoDigitDatePart(this.day);
      const month = _getTwoDigitDatePart(this.month);
      const year = !!this.year ? this.year.toString() : moment().year().toString();
      let selectedDate = moment(`${ year }${ month }${ day }`, 'YYYYMMDD');

      if (!selectedDate.isValid()) {
        return;
      }

      selectedDate = selectedDate.format(this.format);

      if (selectedDate === this.selectedDate) {
        return;
      }

      this.$set(this, 'selectedDate', selectedDate);

      if (selectedDate === this.value) {
        return;
      }

      this.$emit('input', this.selectedDate);
      this.$emit('change');
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

.dui-date-dropdowns {
  align-items: center;
  display: inline-grid;
  grid-column-gap: $_2em;
  grid-template-columns: auto 1fr auto;
  text-align: left;

  &[disabled] {
    opacity: $disabled-opacity;
    pointer-events: none;
  }

  .day-dropdown {
    width: 84px;
  }

  .month-dropdown {
    width: 84px;

    @include screenMoreThan(xs) {
      &.wider {
        width: 150px;
      }
    }
  }

  .year-dropdown {
    width: 100px;
  }
}
</style>
